/**
 * Discreet, dismissible "install to home screen" hint (login screen and account view).
 * Android/Chrome: one button opens the browser's install dialog. iOS Safari: the three Share-menu steps.
 */
import { useEffect, useState } from 'react';
import { currentInstallMode, promptInstall, rememberDismissal, subscribeInstall, type InstallMode } from '../pwa/install.ts';
import { APP_NAME } from '../brand.ts';
import { BrandMark } from './BrandMark.tsx';

export function InstallHint({ className = '' }: { className?: string }) {
  const [mode, setMode] = useState<InstallMode>('none');
  useEffect(() => {
    const update = () => setMode(currentInstallMode());
    update();
    const off = subscribeInstall(update);
    const mq = window.matchMedia?.('(display-mode: standalone)');
    mq?.addEventListener?.('change', update);
    return () => { off(); mq?.removeEventListener?.('change', update); };
  }, []);

  if (mode !== 'prompt' && mode !== 'ios') return null;
  return (
    <aside className={`rounded-xl p-3 flex gap-3 items-start text-left ${className}`} data-testid="install-hint" aria-label="Telepítés a főképernyőre"
      style={{ background: 'rgba(74,158,255,0.07)', border: '1px solid rgba(74,158,255,0.25)' }}>
      <BrandMark size={36} className="flex-shrink-0 mt-0.5" />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-slate-100">Telepítsd a főképernyőre</p>
        {mode === 'prompt' ? (
          <p className="text-xs text-slate-400 mt-0.5">A {APP_NAME} így teljes képernyőn, böngészősáv nélkül indul, saját ikonnal.</p>
        ) : (
          <ol className="text-xs text-slate-300 mt-1 space-y-0.5 list-decimal pl-4" data-testid="install-ios-steps">
            <li>Koppints a Safari <strong>Megosztás</strong> gombjára <span aria-hidden>(⬆︎)</span>.</li>
            <li>Válaszd a <strong>Főképernyőhöz adás</strong> menüpontot.</li>
            <li>Koppints a <strong>Hozzáadás</strong> gombra.</li>
          </ol>
        )}
        <div className="flex gap-2 mt-2">
          {mode === 'prompt' && (
            <button type="button" className="btn-primary" style={{ minHeight: 44 }} data-testid="install-accept"
              onClick={() => { void promptInstall().then(ok => { if (!ok) rememberDismissal(); }); }}>Telepítés</button>
          )}
          <button type="button" className="btn-secondary" style={{ minHeight: 44 }} data-testid="install-dismiss" onClick={rememberDismissal}>
            {mode === 'prompt' ? 'Most nem' : 'Értem, elrejtés'}
          </button>
        </div>
      </div>
    </aside>
  );
}
